import { Modal } from '@/components/common/dialog';
import { ModalButton } from '@/components/common/modal-button';
import { LocationMarkerIcon, MarkerIcon, PhoneIcon, RouteIcon } from '@/components/icons';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import type { FacilityPoiDetailProps } from '@/types/facility';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from '@/stores';
import { AppUtils } from '@/utils/app';
import clsx from 'clsx';
import styles from './styles.module.scss';
import { useTickSoundInteraction } from '@/hooks';

type Props = {
    isOpen?: boolean;
    onClose?: () => void;
    poiDetail?: FacilityPoiDetailProps;
    onLocationConfirmation?: () => void;
    onDirectionGuide?: () => void;
    isShowLocationConfirmation?: boolean;
};

export const FacilityPoiDetailModal = ({
    isOpen = false,
    onClose = () => {},
    poiDetail,
    onLocationConfirmation,
    onDirectionGuide,
    isShowLocationConfirmation = true,
}: Props) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const { t } = useTranslation();
    const { playTick } = useTickSoundInteraction();

    // Measure overflow for description content
    const descriptionRef = useRef<HTMLDivElement | null>(null);
    const [hasOverflow, setHasOverflow] = useState(false);
    const descriptionClassName = useMemo(() => {
        return [
            'mx-[calc(20*var(--vw-unit))] my-[calc(20*var(--vh-unit))] max-h-[calc(281*var(--vh-unit))] whitespace-pre-line',
            hasOverflow ? 'pr-[calc(24*var(--vw-unit))] scrollbar-custom-gray-99938f scrollbar-w-4 overflow-y-auto' : 'overflow-y-hidden',
        ].join(' ');
    }, [hasOverflow]);

    useEffect(() => {
        const el = descriptionRef.current;
        if (!el) return;

        const checkOverflow = () => {
            const targetHeightPx = 281 * (window.innerHeight / 1920);
            setHasOverflow(el.scrollHeight > targetHeightPx);
        };

        checkOverflow();

        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(el);
        window.addEventListener('resize', checkOverflow);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkOverflow);
        };
    }, [poiDetail?.description]);

    const classNames = useMemo(() => {
        return {
            modalwrapperClassName: `fullscreen:p-0! widescreen:fullscreen:pb-0! widescreen:pb-[calc(50*var(--vh-unit))]! ${poiDetail?.description ? 'pb-[calc(200*var(--vh-unit))]!' : 'pb-[calc(320*var(--vh-unit))]!'}`,
            modalClassName: 'max-w-[calc(680*var(--vw-unit))] max-h-[calc(995*var(--vh-unit))]!',
            modalScrollBarClassName: 'max-h-full! overflow-y-hidden!',
        };
    }, [poiDetail?.description]);

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => onClose?.()}
            wrapperClassName={classNames.modalwrapperClassName}
            className={classNames.modalClassName}
            scrollBarClassName={classNames.modalScrollBarClassName}
        >
            <section className="border-not-contrast-bg-gray-f4 border-b-[calc(1*var(--vw-unit))] border-solid">
                {/* title */}
                <section className="border-not-contrast-bg-gray-f4 border-b-[calc(1*var(--vw-unit))] border-solid py-[calc(36*var(--vw-unit))]">
                    <h1
                        data-modal-region
                        tabIndex={0}
                        data-interactive
                        aria-label={poiDetail?.poiName}
                        className="focus-border mx-auto w-11/12 truncate text-center text-[calc(40*var(--vh-unit))] font-bold"
                    >
                        {poiDetail?.poiName}
                    </h1>
                </section>
                <section className="bg-bg-gray-f4-to-dark px-[calc(56*var(--vw-unit))] py-[calc(40*var(--vw-unit))]">
                    <div className="flex items-center gap-[calc(40*var(--vw-unit))]">
                        <div className="flex h-[calc(184*var(--vh-unit))] w-[calc(184*var(--vw-unit))] items-center justify-center overflow-hidden rounded-md">
                            {poiDetail?.poiImage ? (
                                <img src={poiDetail?.poiImage} alt={poiDetail?.poiName} className="h-full w-full rounded-md object-contain object-center" />
                            ) : (
                                <PlaceHolder />
                            )}
                        </div>
                        <div className="flex flex-1 flex-col gap-[calc(12*var(--vh-unit))]">
                            {AppUtils.isValidPhoneNumber(poiDetail?.phoneNumber) && (
                                <div
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            playTick();
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }
                                    }}
                                    data-interactive
                                    aria-label={`${t('common.tts.phone_number')} ${AppUtils.getPhoneNumberTTSText(poiDetail?.phoneNumber ?? '')}`}
                                    className="text-text-main01 focus-border flex items-center gap-[calc(8*var(--vw-unit))]"
                                >
                                    <div>
                                        <PhoneIcon />
                                    </div>
                                    <div>
                                        <p className="number text-[calc(20*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-medium">
                                            {poiDetail?.phoneNumber}
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div
                                tabIndex={0}
                                data-interactive
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        playTick();
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                }}
                                aria-label={t('facility.modal.tts.building_floor_info', {
                                    building: (buildings?.length && buildings?.length > 1) || poiDetail?.showBuildingAddress ? poiDetail?.buildingAddress : '',
                                    floor: AppUtils.getFloorTTSText(poiDetail?.floorName || ''),
                                })}
                                className="focus-border flex items-center gap-[calc(12*var(--vw-unit))]"
                            >
                                <div>
                                    <LocationMarkerIcon width={24} height={24} />
                                </div>
                                <div className="text-text-main01 flex items-center gap-[calc(12*var(--vw-unit))]">
                                    {((buildings?.length && buildings?.length > 1) || poiDetail?.showBuildingAddress) && (
                                        <>
                                            <p className="text-text-sub01 text-[calc(24*var(--vh-unit))] leading-[calc(40*var(--vh-unit))] font-normal">
                                                {poiDetail?.buildingAddress}
                                            </p>
                                            <div className="bg-not-contrast-gray-999 contrast:text-not-contrast-white h-[calc(20*var(--vh-unit))] w-[calc(2*var(--vw-unit))]" />
                                        </>
                                    )}
                                    <p className="number text-[calc(24*var(--vh-unit))] leading-[calc(40*var(--vh-unit))] font-bold">{poiDetail?.floorName}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {poiDetail?.description && (
                        <div
                            tabIndex={0}
                            data-interactive
                            aria-label={poiDetail?.description}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    playTick();
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }}
                            className={clsx(
                                styles['description'],
                                'text-text-main01 bg-bg-white border-not-contrast-white mt-[calc(24*var(--vh-unit))] max-h-[calc(321*var(--vh-unit))] rounded-sm border-[calc(1*var(--vw-unit))] border-solid text-[calc(20*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-normal'
                            )}
                        >
                            <div ref={descriptionRef} className={descriptionClassName}>
                                {poiDetail?.description
                                    ?.replace(/\u00A0/g, ' ') // xoá non-breaking space
                                    ?.replace(/&nbsp;/g, ' ') ?? // phòng trường hợp có entity
                                    ''}
                            </div>
                        </div>
                    )}
                </section>
            </section>
            <section className="flex items-center justify-center gap-[calc(23*var(--vw-unit))] py-[calc(40*var(--vh-unit))]">
                {isShowLocationConfirmation && (
                    <ModalButton
                        data-interactive
                        aria-label={t('facility.modal.tts.buttons.location_check')}
                        variant="primary"
                        icon={<MarkerIcon width={80} height={80} />}
                        size="lg"
                        onClick={() => onLocationConfirmation?.()}
                        role="button"
                        data-role={t('common.data_role.button')}
                    >
                        {t('facility.modal.text.buttons.location_check')}
                    </ModalButton>
                )}
                <ModalButton
                    data-interactive
                    aria-label={t('facility.modal.tts.buttons.direction_guide')}
                    variant="secondary"
                    icon={<RouteIcon width={80} height={80} />}
                    size="lg"
                    onClick={() => onDirectionGuide?.()}
                    role="button"
                    data-role={t('common.data_role.button')}
                >
                    {t('facility.modal.text.buttons.direction_guide')}
                </ModalButton>
            </section>
        </Modal>
    );
};
